import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { ErrorBoundary } from '@sentry/react';
import React, { useRef } from 'react';
import { useEventListener, useLocalStorage } from 'usehooks-ts';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import { cn } from '../../lib/classNameHelpers';
import { localStorageKeys } from '../../lib/localstorageKeys';
import {
  BottomSheetBackdrop,
  BottomSheetContent,
  BottomSheetPortal,
  BottomSheetRoot,
  BottomSheetTitle,
  BottomSheetView,
} from '../BottomSheet/BottomSheet';
import { Button } from '../catalyst-ui-kit/button';
import { StyledDialog } from '../StyledDialog/StyledDialog';

type ResponsiveModalProps = {
  open?: boolean;
  className?: string;
  onClose: () => void;
  panelClassName?: string;
  title?: React.ReactNode;
  hideCloseButton?: boolean;
  backdropClassName?: string;
  children?: React.ReactNode;
  childrenContainerClassName?: string;
};

const ModalErrorFallback: React.FC<{
  error: unknown;
}> = ({ error }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const errorMessage = error instanceof Error ? error.message : String(error);

  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <p className="text-sm font-medium text-contentTertiary">An unexpected error occurred.</p>
      <p className="text-xs font-medium text-contentSecondary">
        A fix is in progress - thanks for your patience.
      </p>

      <Button
        plain
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center text-xs"
      >
        <span className="text-contentSecondary">{isExpanded ? 'Hide' : 'Show'} Error Details</span>
        {isExpanded ? (
          <ChevronUpIcon className="ml-1 size-4" />
        ) : (
          <ChevronDownIcon className="ml-1 size-4" />
        )}
      </Button>

      {isExpanded && (
        <div className="w-full rounded-md bg-backgroundTertiary p-3 text-left">
          <p className="break-words font-mono text-xs text-contentTertiary">
            {errorMessage || 'Unknown error'}
          </p>
        </div>
      )}
    </div>
  );
};

export function ResponsiveModal({
  title,
  open,
  children,
  panelClassName,
  childrenContainerClassName,
  backdropClassName,
  className,
  onClose,
  hideCloseButton,
}: ResponsiveModalProps) {
  const isResizingRef = useRef(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [ignoreMobileOverlay] = useLocalStorage(localStorageKeys.dismissMobileWarning, false);
  const isSm = useTailwindBreakpoint('sm');
  const isLg = useTailwindBreakpoint('lg');

  // Track window resize events we need this to prevent the modal from
  // closing when the user is resizing the window as onPresentedChange
  // is called when we switch between desktop and mobile
  useEventListener('resize', () => {
    isResizingRef.current = true;

    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      isResizingRef.current = false;
    }, 150);
  });

  if (!isLg && !ignoreMobileOverlay) {
    return null;
  }
  return isSm ? (
    <StyledDialog
      key="desktop-modal"
      open={open}
      title={title}
      onClose={onClose}
      className={className}
      panelClassName={panelClassName}
      hideCloseButton={hideCloseButton}
      backdropClassName={backdropClassName}
      childrenContainerClassName={childrenContainerClassName}
    >
      <ErrorBoundary fallback={({ error }) => <ModalErrorFallback error={error} />}>
        {children}
      </ErrorBoundary>
    </StyledDialog>
  ) : (
    <BottomSheetRoot
      key="mobile-modal"
      presented={open}
      onPresentedChange={(presented) => {
        if (!presented && !isResizingRef.current) {
          // Use setTimeout to allow the close animation to complete
          setTimeout(() => {
            onClose();
          }, 300);
        }
      }}
    >
      <BottomSheetPortal>
        <BottomSheetView className={cn('z-40', className)}>
          <BottomSheetBackdrop className={backdropClassName} />
          <BottomSheetContent
            className={cn('max-w-[700px] p-4 grid justify-items-center', panelClassName)}
          >
            {title && (
              <div className="flex w-full items-center justify-between pb-4">
                <BottomSheetTitle className="font-medium text-contentPrimary">
                  {title}
                </BottomSheetTitle>
                {!hideCloseButton && (
                  <Button className="p-0" onClick={onClose} aria-label="Close" plain>
                    <XMarkIcon className="!size-6" />
                  </Button>
                )}
              </div>
            )}
            <div className={cn('w-full', childrenContainerClassName)}>
              <ErrorBoundary fallback={({ error }) => <ModalErrorFallback error={error} />}>
                {children}
              </ErrorBoundary>
            </div>
          </BottomSheetContent>
        </BottomSheetView>
      </BottomSheetPortal>
    </BottomSheetRoot>
  );
}
